import { Modal, Segmented, Table, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePapaParse } from 'react-papaparse';

import NoDataEmpty from '../../components/NoDataEmpty';
import { downloadCsv } from '../../utils/csv';
import OpenClassAPI from '../OpenClassAPI';
import {
  AttendanceStatus,
  CertificateResultStatus,
  OpenClass,
  OpenClassRegistration,
} from '../types/OpenClass';
import { useAttendeeCertificateActions } from '../hooks/useAttendeeCertificateActions';
import {
  StatusAction,
  splitByCertificateIssued,
  splitByPassEligibility,
} from '../utils/attendeeStatusActions';
import { certificateTypeLabel } from '../utils/certificateTypeLabel';
import {
  ClassAttendee,
  SessionCountFilter,
  classAttendanceSummary,
  classAttendeeCsvColumns,
  classAttendeeCsvRows,
  classAttendeeStatus,
  classAttendees,
  filterBySessionCount,
  hasDivergentAttendance,
} from '../utils/openClassAttendees';
import {
  AttendeeCompanyFilter,
  attendeeCompanyFilterOptions,
  filterAttendeesByCompany,
  sortAttendeesByCompany,
} from '../utils/attendeeCompany';
import { AttendeeSelectionBar } from './AttendeeActions';
import AttendeeListToolbar from './AttendeeListToolbar';
import {
  attendeeBesaccCertificateColumn,
  attendeeActionsColumn,
  attendeeCertificateSentColumn,
  attendeeCertificatesColumn,
  attendeeCompanyColumn,
  attendeeNameColumn,
} from './attendeeColumns';
import OpenClassAttendeeStatusBox from './OpenClassAttendeeStatusBox';
import OpenClassSessionStatusTags from './OpenClassSessionStatusTags';
import OpenClassCertificateLanguageModal from './OpenClassCertificateLanguageModal';
import BesaccCertificatePreviewModal from './BesaccCertificatePreviewModal';

const sessionCountFilters: {
  filter: SessionCountFilter;
  labelKey: 'filterAll' | 'filterMultipleSessions' | 'filterSingleSession';
}[] = [
  { filter: 'all', labelKey: 'filterAll' },
  { filter: 'multiple', labelKey: 'filterMultipleSessions' },
  { filter: 'single', labelKey: 'filterSingleSession' },
];

type OpenClassAttendeesModalProps = {
  canUpdate: boolean;
  openClass: OpenClass;
  open: boolean;
  registrations: OpenClassRegistration[];
  onClose: () => void;
  onUpdated: () => Promise<void> | void;
};

const OpenClassAttendeesModal: React.FC<OpenClassAttendeesModalProps> = ({
  canUpdate,
  openClass,
  open,
  registrations,
  onClose,
  onUpdated,
}) => {
  const certificateTypes = useMemo(
    () => openClass.certificateTemplates.map(({ type }) => type),
    [openClass.certificateTemplates],
  );
  const { t } = useTranslation();
  const { jsonToCSV } = usePapaParse();
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<
    AttendeeCompanyFilter[] | null
  >(null);
  const [sessionCount, setSessionCount] = useState<SessionCountFilter>('all');
  const [selectedStatuses, setSelectedStatuses] = useState<string[] | null>(
    null,
  );
  const [selectedCertificateTypes, setSelectedCertificateTypes] = useState<
    string[] | null
  >(null);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const {
    besaccPreview,
    canIssueCertificate,
    cancelLanguagePrompt,
    certificateLanguages,
    closeBesaccPreview,
    confirmLanguagePrompt,
    confirmEmailCertificate,
    confirmEmailCertificates,
    downloadCertificates,
    languagePromptOpen,
    previewBesaccCertificate,
    removeBesaccCertificate,
    runUpdates,
    saving,
    uploadBesaccCertificate,
  } = useAttendeeCertificateActions({
    clearSelection,
    openClass,
    registrations,
    onUpdated,
  });

  const attendees = useMemo(
    () => classAttendees(registrations, openClass.sessions),
    [openClass.sessions, registrations],
  );

  const companyFilterOptions = useMemo(
    () =>
      attendeeCompanyFilterOptions(
        attendees,
        t('components.OpenClassAttendeeActions.privateParticipant'),
      ),
    [attendees, t],
  );

  const companyFiltered = useMemo(
    () => filterAttendeesByCompany(attendees, selectedCompanies),
    [attendees, selectedCompanies],
  );

  const filteredAttendees = useMemo(() => {
    const sessionFiltered = filterBySessionCount(companyFiltered, sessionCount);
    return sessionFiltered.filter(
      (attendee) =>
        (!selectedStatuses?.length ||
          selectedStatuses.includes(classAttendeeStatus(attendee))) &&
        (!selectedCertificateTypes?.length ||
          selectedCertificateTypes.includes(attendee.certificateType)),
    );
  }, [
    companyFiltered,
    selectedCertificateTypes,
    selectedStatuses,
    sessionCount,
  ]);

  const displayedAttendees = useMemo(
    () => sortAttendeesByCompany(filteredAttendees),
    [filteredAttendees],
  );

  const statusOptions = useMemo(
    () =>
      [...new Set(attendees.map(classAttendeeStatus))].sort().map((status) => ({
        label: t(`components.OpenClassAttendeeStatusBox.${status}`),
        value: status,
      })),
    [attendees, t],
  );

  const certificateTypeOptions = useMemo(
    () =>
      certificateTypes.map((certificateType) => ({
        label: certificateTypeLabel(certificateType),
        value: certificateType,
      })),
    [certificateTypes],
  );

  const selectedAttendees = useMemo(() => {
    const selected = new Set(selectedIds);
    return attendees.filter((attendee) =>
      selected.has(attendee.registrationId),
    );
  }, [attendees, selectedIds]);

  const selectedCertificateCount = useMemo(
    () => selectedAttendees.filter(canIssueCertificate).length,
    [canIssueCertificate, selectedAttendees],
  );

  const sessionCountOptions = useMemo(
    () =>
      sessionCountFilters.map(({ filter, labelKey }) => ({
        label: `${t(`components.OpenClassAttendeesModal.${labelKey}`)} (${
          filterBySessionCount(companyFiltered, filter).length
        })`,
        value: filter,
      })),
    [companyFiltered, t],
  );

  // One attendance covers every session the attendee booked, so anyone whose sessions
  // currently disagree would quietly lose that.
  const applyStatus = useCallback(
    async (
      action: StatusAction,
      targets: ClassAttendee[],
      onApplied?: () => void,
    ) => {
      const { writable: unlocked, locked } = splitByCertificateIssued(targets);
      if (locked.length > 0) {
        message.warning(
          t('components.OpenClassAttendeeActions.certificateIssuedSkipped', {
            count: locked.length,
          }),
        );
      }

      // Only the sessions in view can be written, so someone scoped away from all of
      // theirs has nothing to record against.
      const writable =
        action.field === 'attendanceStatus'
          ? unlocked.filter(({ sessions }) => sessions.length > 0)
          : unlocked;

      if (writable.length === 0) {
        message.info(
          t('components.OpenClassAttendeesModal.noSessionsToRecord'),
        );
        return;
      }

      const { eligible, skipped } = splitByPassEligibility(action, writable);
      if (skipped.length > 0) {
        message.warning(
          t('components.OpenClassAttendeeActions.passedSkipped', {
            count: skipped.length,
          }),
        );
      }
      if (eligible.length === 0) {
        onApplied?.();
        return;
      }

      const write = async () => {
        await runUpdates(
          action.field === 'attendanceStatus'
            ? eligible.flatMap((attendee) =>
                attendee.sessions.map(({ sessionId }) =>
                  OpenClassAPI.updateSessionAttendance(
                    sessionId,
                    attendee.registrationId,
                    { attendanceStatus: action.value as AttendanceStatus },
                  ),
                ),
              )
            : eligible.map((attendee) =>
                OpenClassAPI.updateCertificateResult(attendee.registrationId, {
                  certificateResultStatus:
                    action.value as CertificateResultStatus,
                }),
              ),
        );
        onApplied?.();
      };

      const divergent = eligible.filter(hasDivergentAttendance);
      if (action.field === 'attendanceStatus' && divergent.length > 0) {
        Modal.confirm({
          content: t(
            'components.OpenClassAttendeesModal.attendanceOverwriteConfirm',
            { count: divergent.length },
          ),
          okText: t('common.confirm'),
          title: t(
            'components.OpenClassAttendeesModal.attendanceOverwriteTitle',
          ),
          onOk: write,
        });
        return;
      }
      await write();
    },
    [runUpdates, t],
  );

  const onExport = useCallback(() => {
    const csv = jsonToCSV(
      classAttendeeCsvRows(
        filteredAttendees,
        t('components.OpenClassAttendeeActions.privateParticipant'),
        certificateTypeLabel,
      ),
      { columns: classAttendeeCsvColumns },
    );
    downloadCsv(
      `${t('components.OpenClassAttendeesModal.attendeesFileName')}-${
        openClass.course.name
      }-${dayjs(openClass.startAt).format('YYYY-MM-DD')}.csv`.replace(
        /[\s:]+/g,
        '-',
      ),
      csv,
    );
  }, [filteredAttendees, jsonToCSV, openClass, t]);

  const columns: ColumnsType<ClassAttendee> = useMemo(
    () => [
      attendeeNameColumn(
        t,
        (attendee) =>
          classAttendanceSummary(attendee.sessions) === 'notPresent',
      ),
      attendeeCompanyColumn(t),
      {
        // The count leads so someone booked onto several sessions stands out from the rest.
        title: t('components.OpenClassAttendeesModal.columnSessions'),
        key: 'sessions',
        sorter: (left, right) => left.sessions.length - right.sessions.length,
        render: (_, attendee) =>
          attendee.sessions.length === 0 ? (
            '–'
          ) : (
            <div className="flex flex-col items-start gap-1">
              {attendee.sessions.length > 1 && (
                <span className="font-medium text-safeLifeDarker">
                  {`${t('components.OpenClassAttendeesModal.sessionCount', {
                    count: attendee.sessions.length,
                  })}:`}
                </span>
              )}
              <OpenClassSessionStatusTags sessions={attendee.sessions} />
            </div>
          ),
      },
      {
        title: t('components.OpenClassAttendeeActions.columnStatus'),
        key: 'status',
        render: (_, attendee) => (
          <OpenClassAttendeeStatusBox status={classAttendeeStatus(attendee)} />
        ),
      },
      attendeeCertificatesColumn(t, certificateTypes),
      attendeeBesaccCertificateColumn<ClassAttendee>({
        canUpdate,
        saving,
        t,
        onPreview: previewBesaccCertificate,
        onRemove: (attendee) =>
          void removeBesaccCertificate(attendee.registrationId),
        onUpload: (attendee, file) =>
          void uploadBesaccCertificate(attendee.registrationId, file),
      }),
      attendeeCertificateSentColumn<ClassAttendee>({
        t,
      }),
      attendeeActionsColumn<ClassAttendee>({
        canUpdate,
        canIssueCertificate,
        // Nothing to write attendance onto when no session is in view
        isAtStatus: (row, action) =>
          action.field === 'attendanceStatus'
            ? row.sessions.length === 0 ||
              classAttendanceSummary(row.sessions) === action.status
            : row.certificateResultStatus === action.value,
        passBlocked: (row) => {
          if (row.failedSession) {
            return t('components.OpenClassAttendeeActions.sessionFailed');
          }
          if (!row.attendedAllSessions) {
            return t(
              'components.OpenClassAttendeeActions.notAllSessionsAttended',
            );
          }
          return undefined;
        },
        t,
        onApplyStatus: applyStatus,
        onDownload: downloadCertificates,
        onEmail: confirmEmailCertificate,
      }),
    ],
    [
      applyStatus,
      canIssueCertificate,
      canUpdate,
      certificateTypes,
      confirmEmailCertificate,
      downloadCertificates,
      previewBesaccCertificate,
      removeBesaccCertificate,
      saving,
      t,
      uploadBesaccCertificate,
    ],
  );

  return (
    <Modal
      footer={null}
      open={open}
      title={t('components.OpenClassAttendeesModal.title', {
        course: openClass.course.name,
      })}
      width={1280}
      onCancel={onClose}>
      <AttendeeListToolbar
        companyOptions={companyFilterOptions}
        certificateTypeOptions={certificateTypeOptions}
        selectedCompanies={selectedCompanies}
        selectedCertificateTypes={selectedCertificateTypes}
        selectedStatuses={selectedStatuses}
        statusOptions={statusOptions}
        exportDisabled={filteredAttendees.length === 0}
        onCompaniesChange={(companies) => {
          setSelectedCompanies(companies);
          clearSelection();
        }}
        onCertificateTypesChange={(certificateTypes) => {
          setSelectedCertificateTypes(certificateTypes);
          clearSelection();
        }}
        onStatusesChange={(statuses) => {
          setSelectedStatuses(statuses);
          clearSelection();
        }}
        onExport={onExport}>
        <Segmented
          options={sessionCountOptions}
          value={sessionCount}
          onChange={(value) => {
            setSessionCount(value as SessionCountFilter);
            clearSelection();
          }}
        />
      </AttendeeListToolbar>
      <div className="mt-4">
        <Table
          columns={columns}
          dataSource={displayedAttendees}
          loading={saving}
          locale={{ emptyText: <NoDataEmpty /> }}
          rowKey={(attendee) => attendee.registrationId}
          rowSelection={
            canUpdate
              ? {
                  selectedRowKeys: selectedIds,
                  onChange: setSelectedIds,
                  getCheckboxProps: (attendee) => ({
                    disabled: attendee.certificateIssued,
                  }),
                }
              : undefined
          }
        />
      </div>
      {selectedAttendees.length > 0 && (
        <AttendeeSelectionBar
          certificateCount={selectedCertificateCount}
          saving={saving}
          selectedCount={selectedAttendees.length}
          onChangeStatus={(action) =>
            applyStatus(action, selectedAttendees, clearSelection)
          }
          onDownload={() => downloadCertificates(selectedAttendees)}
          onEmail={() => confirmEmailCertificates(selectedAttendees)}
        />
      )}
      <OpenClassCertificateLanguageModal
        languages={certificateLanguages}
        open={languagePromptOpen}
        saving={saving}
        onCancel={cancelLanguagePrompt}
        onConfirm={confirmLanguagePrompt}
      />
      <BesaccCertificatePreviewModal
        certificate={besaccPreview}
        onClose={closeBesaccPreview}
        onRemove={canUpdate ? removeBesaccCertificate : undefined}
        onReplace={canUpdate ? uploadBesaccCertificate : undefined}
      />
    </Modal>
  );
};

export default OpenClassAttendeesModal;
